<template>
  <div class="custom-code-input">
    <div class="input-fields">
      <input
        v-for="(digit, index) in digits"
        type="text"
        :key="index"
        maxlength="1"
        @paste="handlePaste"
        :ref="`input-${index}`"
        v-model="digits[index]"
        @focus="selectAllText($event)"
        @input="handleInput($event, index)"
        :class="{ 'has-value': digits[index] }"
        @keydown="handleKeyDown($event, index)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomCodeInput',
  props: {
    length: {
      type: Number,
      default: 6
    },
    autoFocus: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      digits: Array(this.length).fill('')
    };
  },
  mounted() {
    if (this.autoFocus) {
      this.$nextTick(() => {
        this.focusInput(0);
      })
    }
  },
  watch: {
    digits: {
      deep: true,
      handler(newVal) {
        const code = newVal.join('');
        if (code.length === this.length) {
          this.$emit('complete', code);
        }
      }
    }
  },
  methods: {
    handleInput(event, index) {
      const value = event.target.value;
      if (!/^\d*$/.test(value)) {
        this.digits[index] = '';
        return;
      }

      if (value && index < this.length - 1) {
        this.focusInput(index + 1);
      }

      this.$emit('change', this.digits.join(''));
    },

    handleKeyDown(event, index) {
      if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
        this.focusInput(index - 1);
        return;
      }
      if (event.key === 'Delete' && !this.digits[index] && index < this.length - 1) {
        this.focusInput(index + 1);
        return;
      }

      if (event.key === 'ArrowLeft' && index > 0) {
        this.focusInput(index - 1);
        event.preventDefault();
      } else if (event.key === 'ArrowRight' && index < this.length - 1) {
        this.focusInput(index + 1);
        event.preventDefault();
      }
    },

    handlePaste(event) {
      event.preventDefault();
      const pastedData = (event.clipboardData || window.clipboardData).getData('text').trim();

      if (/^\d+$/.test(pastedData) && pastedData.length === this.length) {
        this.digits = pastedData.split('');
        this.$emit('complete', pastedData);
        this.$emit('change', pastedData);

        // Foca no último campo após colar
        this.focusInput(this.length - 1);
      }
    },

    focusInput(index) {
      this.$nextTick(() => {
        const inputRef = this.$refs[`input-${index}`];
        const el = Array.isArray(inputRef) ? inputRef[0] : inputRef;
        if (el) el.focus();
      });
    },

    selectAllText(event) {
      event.target.select();
    },

    clear() {
      this.digits = Array(this.length).fill('');
      this.focusInput(0);
    },

    setValue(value) {
      if (value && value.length === this.length && /^\d+$/.test(value)) {
        this.digits = value.split('');
        this.$emit('change', value);
        if (value.length === this.length) {
          this.$emit('complete', value);
        }
      }
    }
  }
};
</script>

<style scoped>
.custom-code-input {
  display: flex;
  justify-content: center;
}

.input-fields {
  display: flex;
  gap: 12px;
}

.input-fields input {
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  transition: all 0.3s ease;
  outline: none;
  cursor: text;
}

.input-fields input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.25);
  transform: translateY(-2px);
}

.input-fields input.has-value {
  border-color: #28a745;
  background-color: #f8fff9;
}

.input-fields input:first-child:focus {
  animation: pulse 0.5s ease-in-out;
}

/* Responsividade */
@media (max-width: 576px) {
  .input-fields {
    gap: 8px;
  }

  .input-fields input {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
}
</style>
